/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { DialogService, DIALOG_CONFIG } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthenticationService } from 'rest-client';
import { from, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';

import { LoginDialogComponent } from '@app/modules/shared/components/login-dialog/login-dialog.component';
import { SignUpDialogComponent } from '@app/modules/shared/components/sign-up-dialog/sign-up-dialog.component';
import { environment } from '@env/environment';

import { GoogleTagManagerService } from '../services/analytics/google-tag-manager.service';
import { ErrorReportService } from '../services/error-report/error-report.service';
import {
  StartDisableAction,
  StopDisableAction,
} from '../states/app-state/app.actions';
import {
  RemoveAuthAction,
  SetUserMessageAction,
} from '../states/auth-state/auth.actions';

const FORBIDDEN = 401;
const INTERNAL_SERVER_ERROR = 500;

@UntilDestroy()
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  urlExceptions: string[];

  constructor(
    private store: Store,
    private router: Router,
    private authService: AuthenticationService,
    private translateService: TranslateService,
    private errorReportService: ErrorReportService,
    private dialog: DialogService,
    private googleTagManagerService: GoogleTagManagerService
  ) {
    this.initUrlExceptions();
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.store.dispatch(new StartDisableAction());
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === FORBIDDEN) {
          this.logout();
        } else {
          if (this.showError(request)) {
            this.launchErrorMessage(error);
            if (this.reportError(request, error)) {
              this.reportErrorMessage(error, this.router.url);
            }
          }
        }
        return throwError(error);
      }),
      finalize(() => {
        this.store.dispatch(new StopDisableAction());
      })
    );
  }

  showError(request: HttpRequest<any>): boolean {
    return (
      !document.getElementsByClassName('ngneat-dialog-content').length &&
      !this.urlExceptions.includes(request.url) &&
      !request.url.includes('google')
    );
  }

  reportError(request: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return (
      !this.urlExceptions.includes(request.url) &&
      ((error.status < INTERNAL_SERVER_ERROR && !error?.error?.reason) ||
        error.status >= INTERNAL_SERVER_ERROR) &&
      !request.url.includes('google')
    );
  }

  private launchErrorMessage(error: HttpErrorResponse): void | Observable<any> {
    if (
      error?.error?.error === 'API_ERROR_RESERVATION_OUT_OF_STOCK' &&
      error?.error?.alternative_offer
    ) {
      return throwError(error);
    }

    const errMessage = error?.error?.reason
      ? (error?.error?.reason as string)
      : (this.translateService.instant('common.standardError') as string);
    this.store.dispatch(
      new SetUserMessageAction({
        errorMessage: true,
        message: errMessage,
      })
    );
    window.scroll(0, 0);
  }

  private reportErrorMessage(error: HttpErrorResponse, context: string): void {
    this.errorReportService.sendReport(error, context, this.store.snapshot());
    this.googleTagManagerService.pushBookingErrorOnDataLayer({
      error_type: 'http',
      error_message: error,
    });
  }

  private initUrlExceptions(): void {
    this.urlExceptions = [
      environment.API + '/carservice/estimate',
      environment.API + '/health/error',
      environment.API + '/reservation/booking_session_event',
    ];
  }

  private logout(): void {
    from(this.authService.logout())
      .pipe(untilDestroyed(this))
      .subscribe(() => this.onSuccessLogout());
  }

  private onSuccessLogout(): void {
    this.store.dispatch(new RemoveAuthAction());
    if (!document.getElementsByClassName('ngneat-dialog-content').length) {
      this.openLoginModal();
    }
  }

  private navigateToProducts(): void {
    // console.log('navigate a products del http error');

    this.router.navigate(['/products']);
  }

  openLoginModal(): void {
    this.dialog
      .open(LoginDialogComponent, {
        ...DIALOG_CONFIG,
        size: 'md',
        enableClose: false,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.openSignUpDialog());
  }

  openSignUpDialog(): void {
    this.dialog
      .open(SignUpDialogComponent, {
        ...DIALOG_CONFIG,
        size: 'md',
        enableClose: false,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.openLoginModal());
  }
}
